<template>
  <nav v-if="sections.length" class="blog-toc" aria-label="Contents">
    <p class="blog-toc__title">Contents</p>
    <ul class="blog-toc__list">
      <li
        v-for="section in sections"
        :key="section.id"
        class="blog-toc__item blog-toc__item--level-2"
      >
        <a
          :href="`#${section.id}`"
          class="blog-toc__link"
          :class="{ 'blog-toc__link--active': activeId === section.id }"
          :aria-expanded="section.children.length ? String(isOpen(section)) : undefined"
          @click="onClick($event, section.id)"
        >
          {{ section.text }}
        </a>
        <ul v-if="isOpen(section) && section.children.length" class="blog-toc__sublist">
          <li
            v-for="child in section.children"
            :key="child.id"
            class="blog-toc__item"
            :class="`blog-toc__item--level-${child.level}`"
          >
            <a
              :href="`#${child.id}`"
              class="blog-toc__link"
              :class="{ 'blog-toc__link--active': activeId === child.id }"
              @click="onClick($event, child.id)"
            >
              {{ child.text }}
            </a>
          </li>
        </ul>
      </li>
    </ul>
  </nav>
</template>

<script>
function groupHeadings(headings) {
  const sections = [];
  let current = null;

  headings.forEach((heading) => {
    const startsSection =
      !current ||
      heading.level === 2 ||
      (heading.level === 3 && current.level >= 3);

    if (startsSection) {
      current = { ...heading, children: [] };
      sections.push(current);
      return;
    }

    current.children.push(heading);
  });

  return sections;
}

export default {
  name: "BlogTableOfContents",
  props: {
    headings: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeId: null,
      observer: null,
    };
  },
  computed: {
    sections() {
      return groupHeadings(this.headings);
    },
  },
  watch: {
    headings: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.observe();
          this.scrollToHash();
        });
      },
    },
  },
  beforeUnmount() {
    this.disconnect();
  },
  methods: {
    isOpen(section) {
      if (this.activeId) {
        return (
          section.id === this.activeId ||
          section.children.some((child) => child.id === this.activeId)
        );
      }
      return this.sections[0] && section.id === this.sections[0].id;
    },
    disconnect() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
    observe() {
      this.disconnect();
      if (!this.headings.length || typeof IntersectionObserver === "undefined") {
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible.length) {
            this.activeId = visible[0].target.id;
          }
        },
        {
          root: null,
          rootMargin: "-96px 0px -60% 0px",
          threshold: [0, 1],
        }
      );

      this.headings.forEach((heading) => {
        const element = document.getElementById(heading.id);
        if (element) {
          this.observer.observe(element);
        }
      });
    },
    onClick(event, id) {
      const element = document.getElementById(id);
      if (!element) {
        return;
      }
      event.preventDefault();
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      history.pushState(null, "", `#${id}`);
      this.activeId = id;
    },
    scrollToHash() {
      const id = window.location.hash.replace(/^#/, "");
      if (!id || !this.headings.some((heading) => heading.id === id)) {
        return;
      }
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ block: "start" });
        this.activeId = id;
      }
    },
  },
};
</script>
